/**
 * Pre-seed a freshly-created canvas with the invisible onboarding
 * welcome widget shell.
 *
 * This is the bridge between the workspaces "Welcome" dialog and the
 * canvas-side onboarding system: when the dialog mints a new canvas
 * and routes here, it passes `location.state.seedOnboardingWidget`.
 * Once the Liveblocks document is ready, the user has edit rights, and
 * no `onboarding-welcome` node already exists, we insert one so the
 * screen-docked `OnboardingOverlay` picks it up and starts the step
 * sequence.
 *
 * Retry semantics — why this isn't a simple one-shot:
 *
 * `@liveblocks/react-flow`'s `useLiveblocksFlow` reports `isLoading:
 * false` as soon as the *room* storage loads, but the `flow` storage
 * object that `onNodesChange` writes into is created lazily in a
 * post-commit `useEffect` (`setInitialStorage`). Its `onNodesChange`
 * does `if (!flow) return;` — so on a brand-new room the first
 * `addNode` attempt fires before `flow` exists and is silently
 * dropped. We therefore (re)attempt the add on every render where the
 * node is still absent, using a STABLE node id so the retries
 * reconcile into a single node (the `"add"` change reconciles an
 * existing id rather than duplicating). The seed is considered done
 * only once the node is actually observed in storage.
 *
 * We clear the navigation flag via `replace` so a refresh after
 * dismissal doesn't resurrect it. The existence check (`presetId ===
 * "onboarding-welcome"`) also makes the effect resilient to a stale
 * route navigation racing with a real canvas that already has it.
 */

import { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { ONBOARDING_WELCOME } from "../nodes/widgets/onboarding-welcome";

interface SeedNavigationState {
  seedOnboardingWidget?: boolean;
}

export function useSeedOnboardingWidget() {
  const canvas = useCanvas();
  const location = useLocation();
  const navigate = useNavigate();
  const flags = useFlags<Flags>();

  /**
   * Latch the seed request on first mount.
   *
   * The seed can only run once the Liveblocks document has loaded AND
   * the permission check has resolved `canEdit` — both land a few
   * renders after mount. `location.state` cannot be relied on to
   * survive that window: we strip the flag ourselves after seeding,
   * and unrelated `navigate({ replace: true })` calls on the canvas
   * route also drop it. Capturing intent once, here, keeps it alive.
   */
  const requestedRef = useRef(
    !!(location.state as SeedNavigationState | null | undefined)
      ?.seedOnboardingWidget,
  );

  /**
   * Stable id for the seeded node. Reused across retry attempts so a
   * dropped-then-retried add reconciles into one node instead of
   * spawning duplicates once `flow` storage finally exists.
   */
  const nodeIdRef = useRef(`widget-${uuidv4()}`);

  /**
   * Set once the seeded node has been observed in storage. Latches the
   * seed lifecycle as complete: from then on the effect is inert, even
   * after the node is removed. Without this, finishing the onboarding
   * (which deletes the node) would look identical to "not seeded yet"
   * and the retry path would immediately re-seed — restarting the flow
   * instead of dismissing it.
   */
  const completedRef = useRef(false);

  useEffect(() => {
    if (!requestedRef.current) return;
    // Onboarding gated off — never seed the widget, even if a stale
    // navigation still carries `state.seedOnboardingWidget`.
    if (!flags.onboarding) return;
    if (completedRef.current) return;
    if (!canvas.isReady || canvas.isLoading) return;
    if (!canvas.canEdit) return;

    const seededNode = canvas.nodes.find(
      (n) =>
        n.type === "widgetNode" &&
        (n.data as { presetId?: string } | undefined)?.presetId ===
          ONBOARDING_WELCOME.id,
    );

    if (!seededNode) {
      // Node not in storage yet. (Re)attempt the add — early attempts
      // are silently dropped by `useLiveblocksFlow` until its `flow`
      // LiveObject is initialised in a post-commit effect. The stable
      // `nodeIdRef` id makes this idempotent: once storage exists the
      // add reconciles into a single node, and further attempts hit the
      // `seededNode` branch below instead. Mirrors the canonical
      // widget-node construction (`useCanvasWidgetQuickPlace` /
      // `useCanvasCanvasDropTarget`); the only deliberate divergence is
      // the fixed canvas-origin position.
      canvas.addNode({
        id: nodeIdRef.current,
        type: "widgetNode",
        position: { x: 0, y: 0 },
        width: ONBOARDING_WELCOME.defaultWidth,
        ...(ONBOARDING_WELCOME.resizable?.scaleContent === false
          ? { height: ONBOARDING_WELCOME.defaultHeight }
          : {}),
        ...(ONBOARDING_WELCOME.zIndex !== undefined && {
          zIndex: ONBOARDING_WELCOME.zIndex,
        }),
        // React Flow node-level flags (selectable / deletable /
        // draggable / focusable). The onboarding shell opts out of all.
        ...(ONBOARDING_WELCOME.nodeProps ?? {}),
        data: {
          ...(ONBOARDING_WELCOME.component
            ? {}
            : { html: ONBOARDING_WELCOME.html }),
          label: ONBOARDING_WELCOME.label,
          presetId: ONBOARDING_WELCOME.id,
          ...(ONBOARDING_WELCOME.initialData ?? {}),
        },
      } as Parameters<typeof canvas.addNode>[0]);
      return;
    }

    // Node is confirmed in storage — the seed lifecycle is done. Latch
    // it so a later removal (onboarding finish) can't re-trigger the
    // retry path, and strip the seed flag from history so a manual
    // refresh or back/forward doesn't re-seed either.
    completedRef.current = true;

    const { seedOnboardingWidget: _omit, ...rest } =
      (location.state as SeedNavigationState | null | undefined) ?? {};
    navigate(location.pathname + location.search, {
      replace: true,
      state: Object.keys(rest).length > 0 ? rest : null,
    });
  }, [
    flags.onboarding,
    canvas.isReady,
    canvas.isLoading,
    canvas.canEdit,
    canvas.nodes,
    canvas.addNode,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);
}
